/**
 * 배송 실시간 신호 — 서버가 사용자 알림 채널(`buyer_{id}_notifications` / `supplier_{id}_notifications`)에 보내는
 * `shipment-updated` 이벤트의 계약. 구독은 앱마다 자기 supabase 클라이언트로 하고, 여기서는 이름 · 모양 · 어떤 쿼리를
 * 다시 읽을지만 정한다.
 *
 * 신호에는 상태 · 버전만 있다(개인 정보 없음). 값은 화면이 API 로 다시 읽는다.
 */
import type { QueryClient } from "@tanstack/react-query";

/** 서버 NotificationRealtimeEventEnum.SHIPMENT_UPDATED 와 같은 이름 */
export const SHIPMENT_UPDATED_EVENT = "shipment-updated";

export interface ShipmentUpdatedPayload {
  shipmentId: string;
  subjectKind: string;
  subjectId: string;
  shipmentNo: number;
  eventType: string;
  deliveryStatus: string;
  acceptanceStatus: string;
  version: number;
  occurredAt: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** 모양이 틀린 신호는 null — 화면은 조용히 무시하면 된다 */
export function parseShipmentUpdated(
  payload: unknown,
): ShipmentUpdatedPayload | null {
  if (!isRecord(payload)) return null;
  const {
    shipmentId,
    subjectKind,
    subjectId,
    shipmentNo,
    eventType,
    deliveryStatus,
    acceptanceStatus,
    version,
    occurredAt,
  } = payload;
  if (
    typeof shipmentId !== "string" ||
    typeof subjectKind !== "string" ||
    typeof subjectId !== "string" ||
    typeof shipmentNo !== "number" ||
    typeof eventType !== "string" ||
    typeof deliveryStatus !== "string" ||
    typeof acceptanceStatus !== "string" ||
    typeof version !== "number" ||
    typeof occurredAt !== "string"
  ) {
    return null;
  }
  return {
    shipmentId,
    subjectKind,
    subjectId,
    shipmentNo,
    eventType,
    deliveryStatus,
    acceptanceStatus,
    version,
    occurredAt,
  };
}

/**
 * 송장 데이터를 담은 React Query 키인가. Orval 이 만드는 키는 URL 경로 문자열로 시작한다
 * (`/contracts/{id}/shipments`, `/shipping/shipments`, `/external/storefronts/my-shipments` …).
 * 신호를 받으면 이 키들만 다시 읽는다.
 */
export function isShipmentQueryKey(queryKey: readonly unknown[]): boolean {
  const first = queryKey[0];
  return (
    typeof first === "string" &&
    /\/(shipping|shipments|my-shipments)(\/|$|\?)/.test(first)
  );
}

/** 송장 데이터를 담은 질의를 모두 다시 읽는다(화면에 떠 있는 것만 바로 — 나머지는 낡은 것으로 표시된다) */
export function invalidateShipmentQueries(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isShipmentQueryKey(query.queryKey),
  });
}

/** 창 복귀 · 네트워크 복구 신호가 겹쳐 와도 이 간격 안에서는 한 번만 다시 읽는다 */
export const SHIPMENT_REFETCH_MIN_INTERVAL_MS = 15_000;

/**
 * 실시간 신호는 연결이 끊긴 동안의 변화를 알려 주지 못한다 — 사용자가 창으로 돌아오거나 네트워크가 돌아오면 송장 질의만 다시 읽는다.
 * 앱 전체의 `refetchOnWindowFocus: false` · `staleTime` 설정은 그대로 두고, 송장 질의에만 «창 복귀 시 재조회» 를 켜는 셈이다
 * (질의 하나하나에 옵션을 달지 않아도 새로 생기는 송장 질의까지 같은 기준 `isShipmentQueryKey` 로 덮인다).
 * 돌려주는 함수를 부르면 듣기를 그만둔다.
 */
export function watchShipmentReturn(queryClient: QueryClient): () => void {
  if (typeof document === "undefined" || typeof window === "undefined")
    return () => undefined;

  let lastAt = Number.NEGATIVE_INFINITY;
  const refetch = () => {
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastAt < SHIPMENT_REFETCH_MIN_INTERVAL_MS) return;
    lastAt = now;
    void invalidateShipmentQueries(queryClient);
  };

  document.addEventListener("visibilitychange", refetch);
  window.addEventListener("focus", refetch);
  window.addEventListener("online", refetch);
  return () => {
    document.removeEventListener("visibilitychange", refetch);
    window.removeEventListener("focus", refetch);
    window.removeEventListener("online", refetch);
  };
}

/**
 * 채널에 «들어간 순간»(서버가 join 을 받아 준 순간)을 알려 받는다 — 처음 들어갈 때와 연결이 끊겼다 다시 들어갈 때 모두.
 *
 * `channel.subscribe(콜백)` 의 SUBSCRIBED 콜백에 기대면 안 된다. supabase 는 같은 이름의 채널을 하나로 합치는데(`client.channel(이름)` 은
 * 이미 있는 인스턴스를 돌려준다), 다른 훅이 먼저 들어가 있으면 두 번째 `subscribe(콜백)` 은 콜백을 등록하지 않고 그냥 돌아온다.
 * 다시 들어갈 때의 콜백도 맨 처음 구독한 쪽 것만 불린다. 그래서 가입 응답(`joinPush`)에 직접 듣는다:
 * realtime-js 의 Push 는 이미 받은 응답이면 바로 콜백을 부르고, 이후 재가입(`resend`)에도 같은 훅을 다시 부른다.
 *
 * 돌려주는 함수를 부르면 그 뒤로는 콜백을 부르지 않는다(채널은 정리할 때 함께 버려진다).
 * `joinPush` 가 없는 채널(옛 버전 · 가짜)은 아무것도 하지 않는다.
 */
export interface JoinObservableChannel {
  joinPush?: {
    receive: (status: string, callback: () => void) => unknown;
  };
}

export function onChannelJoined(
  channel: JoinObservableChannel,
  callback: () => void,
): () => void {
  let active = true;
  channel.joinPush?.receive("ok", () => {
    if (active) callback();
  });
  return () => {
    active = false;
  };
}
